import { useEffect, useState } from "react";
import axios from "axios";
import { Check, Sparkles, Zap, Crown, Loader2, ShieldCheck, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";
import { trackIds } from "@/lib/track";
import SectionHeading, { SectionDivider } from "@/components/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  ar: {
    eyebrow: "باقات الاشتراك",
    title: "اختر الباقة التي تناسب حجم عملك",
    subtitle:
      "ابدأ مجانًا، أو اشترك شهريًا/سنويًا للحصول على اقتراحات تسعير ذكية غير محدودة بسعر ثابت وشفاف — دفع مرة واحدة عبر Tap.",
    free: {
      name: "مجاني",
      price: "0",
      per: "دائمًا",
      ribbon: "ابدأ هنا",
      features: [
        "٣ اقتراحات ذكاء اصطناعي شهريًا",
        "حاسبة التسعير الكاملة",
        "تصدير عرض PDF",
        "دعم اللغتين العربية والإنجليزية",
      ],
      cta: "استخدم مجانًا",
    },
    monthly: {
      name: "اشتراك شهري",
      price: "99",
      per: "شهريًا",
      ribbon: "الأكثر مرونة",
      features: [
        "اقتراحات AI غير محدودة",
        "تصدير PDF غير محدود",
        "حفظ العروض كلها",
        "أولوية في تحديثات الحاسبة",
        "٣٠ يومًا كاملة دون إعادة شحن",
      ],
      cta: "اشترك شهريًا",
    },
    yearly: {
      name: "اشتراك سنوي",
      price: "499",
      per: "سنويًا",
      ribbon: "وفّر ٥٨٩ ريال",
      features: [
        "كل مزايا الاشتراك الشهري",
        "اقتراحات AI غير محدودة لسنة كاملة",
        "توفير ما يعادل ٦ أشهر مجانًا",
        "دعم مباشر ذو أولوية",
        "٣٦٥ يومًا بدون قلق",
      ],
      cta: "اشترك سنويًا",
    },
    sar: "ريال",
    emailLabel: "بريدك الإلكتروني",
    emailPlaceholder: "you@example.com",
    emailHint: "نربط اشتراكك ببريدك — لا تنتهي اقتراحاتك.",
    popular: "الأكثر شيوعًا",
    bestValue: "الأوفر",
    oneTime: "دفعة واحدة عبر Tap",
    secure: "دفع آمن ومشفّر عبر Tap Payments",
    activeNow: "اشتراكك مفعّل",
    activeDays: (d) => `متبقّي ${d} يومًا`,
    loading: "جارٍ التحويل...",
  },
  en: {
    eyebrow: "Subscription plans",
    title: "Pick the plan that fits your freelance workflow",
    subtitle:
      "Start free, or subscribe monthly/yearly for unlimited AI pricing suggestions at a flat, transparent price — one-time payment via Tap.",
    free: {
      name: "Free",
      price: "0",
      per: "forever",
      ribbon: "Start here",
      features: [
        "3 AI pricing suggestions / month",
        "Full pricing calculator",
        "PDF proposal export",
        "Arabic & English support",
      ],
      cta: "Use for free",
    },
    monthly: {
      name: "Monthly",
      price: "99",
      per: "/month",
      ribbon: "Most flexible",
      features: [
        "Unlimited AI suggestions",
        "Unlimited PDF exports",
        "Save all your quotes",
        "Priority access to new features",
        "Full 30 days — no recharge",
      ],
      cta: "Subscribe monthly",
    },
    yearly: {
      name: "Yearly",
      price: "499",
      per: "/year",
      ribbon: "Save 589 SAR",
      features: [
        "All monthly plan benefits",
        "Unlimited AI for a full year",
        "~6 months free vs monthly",
        "Priority direct support",
        "365 worry-free days",
      ],
      cta: "Subscribe yearly",
    },
    sar: "SAR",
    emailLabel: "Your email",
    emailPlaceholder: "you@example.com",
    emailHint: "We link your subscription to your email — your access never expires early.",
    popular: "Most popular",
    bestValue: "Best value",
    oneTime: "One-time payment via Tap",
    secure: "Encrypted secure payment via Tap Payments",
    activeNow: "Your subscription is active",
    activeDays: (d) => `${d} days remaining`,
    loading: "Redirecting...",
  },
};

export default function PricingPlans({ lang = "ar", theme = "dark" }) {
  const dark = theme === "dark";
  const ar = lang === "ar";
  const L = ar ? T.ar : T.en;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [pkgs, setPkgs] = useState(null);
  const arabicFont = ar ? "font-arabic" : "font-outfit";

  useEffect(() => {
    axios.get(`${API}/payments/packages`).then((r) => setPkgs(r.data.packages || [])).catch(() => setPkgs([]));
  }, []);
  const isOn = (id) => !!pkgs?.some((p) => p.id === id);
  const creditPacks = (pkgs || []).filter((p) => p.type === "credits");

  useEffect(() => {
    const savedEmail = localStorage.getItem("pl_email") || "";
    const savedName = localStorage.getItem("pl_name") || "";
    if (savedEmail) setEmail(savedEmail);
    if (savedName) setName(savedName);
  }, []);

  // Poll subscription status whenever email changes
  useEffect(() => {
    const em = (email || "").trim().toLowerCase();
    if (!em || !em.includes("@")) {
      setSubscription(null);
      return;
    }
    const fetchSub = async () => {
      try {
        const { data } = await axios.get(`${API}/user/subscription`, { params: { email: em } });
        setSubscription(data && data.active ? data : null);
      } catch {
        setSubscription(null);
      }
    };
    fetchSub();
  }, [email]);

  const checkout = async (packageId) => {
    const em = (email || "").trim().toLowerCase();
    if (!em || !em.includes("@")) {
      toast.error(ar ? "أدخل بريدًا إلكترونيًا صحيحًا" : "Enter a valid email");
      document.querySelector('[data-testid="pricing-email"]')?.focus();
      return;
    }
    setLoading(packageId);
    try {
      localStorage.setItem("pl_email", em);
      const trimmedName = (name || "").trim();
      if (trimmedName) localStorage.setItem("pl_name", trimmedName);
      const { data } = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        email: em,
        origin_url: window.location.origin,
        ...trackIds(),
      });
      // Open Tap checkout in a new window when running inside an iframe (prevents CSP blocks
      // and keeps the host site intact). Normal page → same-tab redirect.
      const inIframe = typeof window !== "undefined" && window.self !== window.top;
      if (inIframe) {
        const w = window.open(data.checkout_url, "_blank", "noopener,noreferrer");
        if (!w) window.location.href = data.checkout_url; // popup blocked → fallback
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(ar ? "تعذّر بدء الدفع — حاول لاحقًا" : "Failed to start checkout — try again");
      setLoading(null);
    }
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-plans-section"
      dir={ar ? "rtl" : "ltr"}
      className={`relative ${dark ? "bg-[#1f2a47]" : "bg-[#f3f8ff]"}`}
    >
      <SectionDivider theme={theme} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <SectionHeading
          lang={lang}
          theme={theme}
          icon={<Crown className="h-3.5 w-3.5" />}
          eyebrow={L.eyebrow}
          title={L.title}
          subtitle={L.subtitle}
        />

        {/* Active subscription banner */}
        {subscription && subscription.active ? (
          <div
            data-testid="pricing-active-banner"
            className={`mt-8 rounded-2xl border p-4 flex items-center gap-3 ${
              dark
                ? "bg-[#18c98f]/10 border-[#18c98f]/40 text-white"
                : "bg-[#18c98f]/10 border-[#12a777]/40 text-[#182138]"
            }`}
          >
            <div className="h-10 w-10 rounded-full bg-[#18c98f] text-white inline-flex items-center justify-center">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className={`font-bold text-sm sm:text-base ${arabicFont}`}>
                {L.activeNow} —{" "}
                <span className="text-[#18c98f]">
                  {subscription.plan === "yearly_unlimited" ? L.yearly.name : L.monthly.name}
                </span>
              </div>
              <div className={`text-xs mt-0.5 ${dark ? "text-white/70" : "text-[#2d2c35]/70"} ${arabicFont}`}>
                {L.activeDays(subscription.days_remaining ?? 0)}
              </div>
            </div>
          </div>
        ) : null}

        {/* Email + Name collector */}
        <div className={`mt-8 rounded-2xl p-4 sm:p-5 border ${dark ? "bg-[#182138] border-[#2e3a5c]" : "bg-white border-[#e5e5ea]"}`}>
          <label
            htmlFor="pricing-name"
            className={`block text-xs font-bold uppercase tracking-widest ${
              dark ? "text-white/60" : "text-[#2d2c35]/60"
            } ${arabicFont}`}
          >
            {ar ? "اسمك" : "Your name"}
          </label>
          <input
            id="pricing-name"
            data-testid="pricing-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              const v = (name || "").trim();
              if (v) localStorage.setItem("pl_name", v);
            }}
            placeholder={ar ? "مثال: عزيز" : "e.g. Aziz"}
            maxLength={100}
            className={`mt-2 w-full h-11 px-4 rounded-xl border text-sm ${
              dark
                ? "bg-[#1f2a47] border-[#2e3a5c] text-white placeholder:text-white/40"
                : "bg-white border-[#e5e5ea] text-[#2d2c35] placeholder:text-[#2d2c35]/40"
            } focus:outline-none focus:border-[#18c98f]`}
          />
          <label
            htmlFor="pricing-email"
            className={`block mt-4 text-xs font-bold uppercase tracking-widest ${
              dark ? "text-white/60" : "text-[#2d2c35]/60"
            } ${arabicFont}`}
          >
            {L.emailLabel}
          </label>
          <input
            id="pricing-email"
            data-testid="pricing-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={L.emailPlaceholder}
            dir="ltr"
            className={`mt-2 w-full h-11 px-4 rounded-xl border text-sm ${
              dark
                ? "bg-[#1f2a47] border-[#2e3a5c] text-white placeholder:text-white/40"
                : "bg-white border-[#e5e5ea] text-[#2d2c35] placeholder:text-[#2d2c35]/40"
            } focus:outline-none focus:border-[#18c98f]`}
          />
          <p className={`mt-2 text-[11px] ${dark ? "text-white/50" : "text-[#2d2c35]/60"} ${arabicFont}`}>
            {ar
              ? "اسمك سيظهر مع تقييمك إن قمت بتقييم الخدمة لاحقًا."
              : "Your name will be shown with your rating if you rate the service later."}
          </p>
          <p className={`mt-1 text-[11px] ${dark ? "text-white/50" : "text-[#2d2c35]/60"} ${arabicFont}`}>
            {L.emailHint}
          </p>
        </div>

        {/* Plan cards */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          <PlanCard
            testId="plan-free"
            plan={L.free}
            icon={<Zap className="h-5 w-5" />}
            dark={dark}
            arabicFont={arabicFont}
            ar={ar}
            sar={L.sar}
            highlight={false}
            ctaOnClick={() => {
              document.getElementById("calc")?.scrollIntoView({ behavior: "smooth" });
            }}
            loading={false}
            disabled={false}
          />
          {creditPacks.map((p) => (
            <PlanCard
              key={p.id}
              testId={`plan-${p.id}`}
              plan={{
                name: ar ? p.name_ar : p.name_en,
                price: String(Number(p.amount)),
                per: ar ? "دفعة واحدة" : "one-time",
                features: [ar ? p.usage_ar : p.usage_en, ar ? p.validity_ar : p.validity_en, ar ? "بدون تجديد تلقائي" : "No auto-renewal"],
                cta: ar ? "اشترِ الرصيد" : "Buy credits",
              }}
              icon={<Zap className="h-5 w-5" />}
              dark={dark}
              arabicFont={arabicFont}
              ar={ar}
              sar={L.sar}
              highlight={false}
              oneTimeNote={L.oneTime}
              ctaOnClick={() => checkout(p.id)}
              loading={loading === p.id}
              loadingLabel={L.loading}
              disabled={false}
            />
          ))}
          {isOn("sub_monthly") && <PlanCard
            testId="plan-monthly"
            plan={L.monthly}
            icon={<Sparkles className="h-5 w-5" />}
            dark={dark}
            arabicFont={arabicFont}
            ar={ar}
            sar={L.sar}
            highlight={true}
            ribbon={L.popular}
            oneTimeNote={L.oneTime}
            ctaOnClick={() => checkout("sub_monthly")}
            loading={loading === "sub_monthly"}
            loadingLabel={L.loading}
            disabled={subscription?.active && subscription?.plan === "monthly_unlimited"}
          />}
          {isOn("sub_yearly") && <PlanCard
            testId="plan-yearly"
            plan={L.yearly}
            icon={<Crown className="h-5 w-5" />}
            dark={dark}
            arabicFont={arabicFont}
            ar={ar}
            sar={L.sar}
            highlight={false}
            ribbon={L.bestValue}
            oneTimeNote={L.oneTime}
            ctaOnClick={() => checkout("sub_yearly")}
            loading={loading === "sub_yearly"}
            loadingLabel={L.loading}
            disabled={subscription?.active && subscription?.plan === "yearly_unlimited"}
          />}
        </div>

        {/* Secure footer */}
        <div className={`mt-8 flex items-center justify-center gap-2 text-[11px] sm:text-xs ${dark ? "text-white/55" : "text-[#2d2c35]/65"} ${arabicFont}`}>
          <ShieldCheck className="h-3.5 w-3.5 text-[#18c98f]" />
          {L.secure}
        </div>
      </div>
    </section>
  );
}

function PlanCard({
  testId,
  plan,
  icon,
  dark,
  arabicFont,
  ar,
  sar,
  highlight,
  ribbon,
  oneTimeNote,
  ctaOnClick,
  loading,
  loadingLabel,
  disabled,
}) {
  const borderCls = highlight
    ? "border-[#18c98f] shadow-[0_18px_50px_-16px_rgba(24,201,143,0.5)]"
    : dark
    ? "border-[#2e3a5c]"
    : "border-[#e5e5ea]";
  const bgCls = highlight
    ? dark
      ? "bg-gradient-to-br from-[#1f2a47] to-[#182138]"
      : "bg-gradient-to-br from-white to-[#f3f8ff]"
    : dark
    ? "bg-[#182138]"
    : "bg-white";

  return (
    <div
      data-testid={testId}
      className={`relative rounded-3xl border-2 p-6 sm:p-7 flex flex-col ${bgCls} ${borderCls} transition-all hover:scale-[1.015]`}
    >
      {ribbon ? (
        <div
          className={`absolute -top-3 ${ar ? "right-6" : "left-6"} inline-flex items-center gap-1 rounded-full bg-[#18c98f] text-white px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest ${arabicFont}`}
        >
          <Sparkles className="h-3 w-3" />
          {ribbon}
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <div className={`h-9 w-9 rounded-xl inline-flex items-center justify-center ${highlight ? "bg-[#18c98f]/20 text-[#18c98f]" : dark ? "bg-[#1f2a47] text-[#18c98f]" : "bg-[#f3f8ff] text-[#12a777]"}`}>
          {icon}
        </div>
        <div className={`text-lg font-extrabold ${arabicFont} ${dark ? "text-white" : "text-[#182138]"}`}>
          {plan.name}
        </div>
      </div>

      <div className="mt-5 flex items-baseline gap-2" dir="ltr">
        <span className={`text-5xl font-black font-mono-num ${highlight ? "text-[#18c98f]" : dark ? "text-white" : "text-[#182138]"}`}>
          {plan.price}
        </span>
        <span className={`text-sm font-bold ${dark ? "text-white/70" : "text-[#2d2c35]/70"}`}>{sar}</span>
      </div>
      <div className={`mt-1 text-xs ${dark ? "text-white/55" : "text-[#2d2c35]/60"} ${arabicFont}`}>{plan.per}</div>

      <ul className="mt-6 space-y-2.5 flex-1">
        {plan.features.map((f, i) => (
          <li key={i} className={`flex items-start gap-2 text-sm ${dark ? "text-white/85" : "text-[#2d2c35]"} ${arabicFont}`}>
            <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${highlight ? "bg-[#18c98f] text-white" : dark ? "bg-[#18c98f]/15 text-[#18c98f]" : "bg-[#18c98f]/15 text-[#12a777]"}`}>
              <Check className="h-3 w-3" />
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {oneTimeNote ? (
        <div className={`mt-5 inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-widest ${dark ? "text-white/50" : "text-[#2d2c35]/55"} ${arabicFont}`}>
          <InfinityIcon className="h-3.5 w-3.5 text-[#18c98f]" />
          {oneTimeNote}
        </div>
      ) : null}

      <button
        data-testid={`${testId}-cta`}
        onClick={ctaOnClick}
        disabled={loading || disabled}
        className={`mt-5 w-full h-12 rounded-full text-sm font-extrabold inline-flex items-center justify-center gap-2 transition-all ${
          disabled
            ? "bg-[#18c98f]/40 text-[#182138] cursor-not-allowed"
            : highlight
            ? "bg-[#18c98f] text-white hover:brightness-110 shadow-[0_8px_22px_-6px_rgba(24,201,143,0.6)]"
            : dark
            ? "bg-[#1f2a47] border border-[#2e3a5c] text-white hover:border-[#18c98f] hover:text-[#18c98f]"
            : "bg-white border border-[#e5e5ea] text-[#182138] hover:border-[#18c98f] hover:text-[#12a777]"
        } ${loading || disabled ? "opacity-70" : ""} ${arabicFont}`}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {loadingLabel}
          </>
        ) : disabled ? (
          ar ? "مفعّل حاليًا ✓" : "Currently active ✓"
        ) : (
          plan.cta
        )}
      </button>
    </div>
  );
}
